import {
  parseApiDateValue,
  getPaymentTemplateStageLabel,
  resolvePaymentTemplateFromPercentages,
} from "../Agreements/agreementFormHelpers";

export const PAYMENT_STATUS_LABELS = {
  paid_advance: "Оплачен Аванс",
  paid_advance_overdue: "Оплачен Аванс с просрочкой",
  overdue_advance: "Просрочен Аванс",
  expected_ODGE: "Ожидается ОДГЭ",
  expected_OPGE: "Ожидается ОПГЭ",
  expected_advance: "Ожидается Аванс",
  overdue_OPGE: "Просрочен ОПГЭ",
  overdue_ODGE: "Просрочен ОДГЭ",
  paid: "Оплачен",
  expected: "Ожидается",
  overdue: "Просрочен",
  paid_overdue: "Оплачен с просрочкой",
  active: "Активно",
  draft: "Черновик",
  closed: "Закрыто",
};

export const WORKFLOW_STATUS_LABELS = {
  completed: "Завершён",
  in_progress: "В процессе",
  not_started: "Не начат",
  pending: "Ожидает",
  rejected: "Отклонён",
};

export const TASK_STATUS_LABELS = {
  in_progress: "В работе",
  completed: "Завершено",
  not_started: "Не начат",
  pending: "Ожидает",
};

/** Этапы процесса по макету (fallback, если API не вернул workflow_stages) */
export const DEFAULT_WORKFLOW_STAGES = [
  { stage_name: "Задание на проектирование", status: "not_started" },
  { stage_name: "КП субподрядчика", status: "not_started" },
  { stage_name: "Соглашение", status: "not_started" },
  { stage_name: "Договор с субподрядчиком", status: "not_started" },
  { stage_name: "Выдача ИРД", status: "not_started" },
  { stage_name: "Оплата аванса", status: "not_started" },
  { stage_name: "Подписание АВР до экспертизы", status: "not_started" },
  { stage_name: "Подписание АВР после экспертизы", status: "not_started" },
  { stage_name: "Оценка", status: "not_started" },
];

export const normalizeWorkflowStages = (workflowStages) => {
  const fromApi = Array.isArray(workflowStages) ? workflowStages : [];
  if (fromApi.length === 0) {
    return DEFAULT_WORKFLOW_STAGES.map((stage) => ({ ...stage }));
  }
  return fromApi.map((stage) => ({
    stage_name: stage.stage_name || "—",
    status: stage.status || "not_started",
    executor_name: stage.executor_name,
    role_type: stage.role_type,
    decided_at: stage.decided_at,
  }));
};

export const splitWorkflowStagesLayout = (stages) => {
  const irdIndex = stages.findIndex((s) =>
    String(s.stage_name || "").toLowerCase().includes("ирд"),
  );
  const advanceIndex = stages.findIndex((s) =>
    String(s.stage_name || "").toLowerCase().includes("аванс"),
  );

  if (irdIndex >= 0 && advanceIndex >= 0) {
    const forkStart = Math.min(irdIndex, advanceIndex);
    const forkEnd = Math.max(irdIndex, advanceIndex);
    return {
      before: stages.slice(0, forkStart),
      parallel: [stages[irdIndex], stages[advanceIndex]],
      after: stages.slice(forkEnd + 1),
    };
  }

  return { before: stages, parallel: [], after: [] };
};

export const getCurrentWorkflowStageFromList = (stages) => {
  const inProgress = stages.find((s) => s.status === "in_progress");
  if (inProgress) return inProgress;
  const pending = stages.find(
    (s) => s.status !== "completed" && s.status !== "rejected",
  );
  if (pending) return pending;
  return stages[stages.length - 1] || null;
};

export const isContractStageCompleted = (stages) => {
  const contractStage = stages.find((s) =>
    String(s.stage_name || "").toLowerCase().includes("договор"),
  );
  return contractStage?.status === "completed";
};

export const formatUserDisplayName = (user) => {
  if (!user) return "";
  const name =
    user.full_name?.trim() ||
    [
      user.surname || user.lastName,
      user.name || user.firstName,
      user.patronymic || user.middleName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
  return name || (user.id != null ? `Пользователь #${user.id}` : "");
};

const toPerformerIdList = (value) => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null || item === "") return null;
        if (typeof item === "object") {
          return item.id ?? item.user_id ?? item.employee_id ?? item.performer_id;
        }
        return item;
      })
      .filter((id) => id != null && id !== "");
  }
  return [value];
};

const pickDisplayName = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
};

export const resolvePerformerDisplayName = (
  agreement,
  performerNameById = {},
) => {
  const fromApi = Array.isArray(agreement?.performer_names)
    ? agreement.performer_names.filter(Boolean)
    : typeof agreement?.performer_names === "string" &&
        agreement.performer_names.trim()
      ? [agreement.performer_names.trim()]
      : [];
  if (fromApi.length > 0) return fromApi.join(", ");

  const nestedNames = Array.isArray(agreement?.performers)
    ? agreement.performers
        .map((person) =>
          pickDisplayName(
            person?.full_name,
            person?.name,
            formatUserDisplayName(person),
          ),
        )
        .filter(Boolean)
    : [];
  if (nestedNames.length > 0) return nestedNames.join(", ");

  const ids = [
    ...toPerformerIdList(agreement?.performer_ids),
    ...toPerformerIdList(agreement?.performer_id),
    ...toPerformerIdList(agreement?.employee_id),
    ...toPerformerIdList(agreement?.user_id),
  ];
  const uniqueIds = [
    ...new Set(ids.map(Number).filter((id) => Number.isFinite(id))),
  ];
  const fromIds = uniqueIds
    .map((id) => performerNameById[id])
    .filter(Boolean);
  if (fromIds.length > 0) return fromIds.join(", ");

  if (uniqueIds.length > 0) {
    return uniqueIds.map((id) => `Пользователь #${id}`).join(", ");
  }

  return null;
};

export const getAgreementDisplayName = (agreement, fallbackName = null) => {
  const fromSummary =
    agreement?.counterparty_summary?.display_name ||
    agreement?.counterparty_summary?.full_name ||
    agreement?.counterparty_summary?.full_company_name ||
    agreement?.counterparty_summary?.short_company_name;
  if (fromSummary) return fromSummary;
  const performerName = resolvePerformerDisplayName(agreement);
  if (performerName) return performerName;
  return fallbackName || `Соглашение №${agreement?.number ?? agreement?.id ?? "—"}`;
};

const isEmployeePerformerType = (performerType) =>
  performerType === "contract_employee" || performerType === "staff_employee";

const getLinkedCounterpartyId = (agreement) => {
  if (agreement?.counterparty_id != null && agreement.counterparty_id !== "") {
    return agreement.counterparty_id;
  }
  if (
    !isEmployeePerformerType(agreement?.performer_type) &&
    agreement?.company_id != null &&
    agreement.company_id !== ""
  ) {
    return agreement.company_id;
  }
  return (
    agreement?.counterparty_summary?.id ??
    agreement?.counterparty?.id ??
    null
  );
};

export const getCounterpartyDisplayName = (
  agreement,
  counterpartyNameById = {},
  performerNameById = {},
) => {
  const counterpartySummary =
    agreement?.counterparty_summary ||
    agreement?.counterparty ||
    agreement?.company;

  const fromFields = pickDisplayName(
    counterpartySummary?.display_name,
    counterpartySummary?.full_name,
    counterpartySummary?.full_company_name,
    counterpartySummary?.short_company_name,
    counterpartySummary?.short_name,
    agreement?.counterparty_name,
    agreement?.performer_name,
    agreement?.company_name,
  );
  if (fromFields) return fromFields;

  const linkedId = getLinkedCounterpartyId(agreement);
  if (linkedId != null && counterpartyNameById[Number(linkedId)]) {
    return counterpartyNameById[Number(linkedId)];
  }

  const performerName = resolvePerformerDisplayName(
    agreement,
    performerNameById,
  );
  if (performerName) return performerName;

  if (linkedId != null) return `Контрагент #${linkedId}`;
  return "—";
};

export const getAgreementSectionLabels = (agreement) => {
  const blockDetails = Array.isArray(agreement?.agreement_blocks_detail)
    ? agreement.agreement_blocks_detail
    : [];
  if (blockDetails.length > 0) {
    return blockDetails.map((b) => b?.block_name).filter(Boolean);
  }
  const blockIds = Array.isArray(agreement?.block_ids) ? agreement.block_ids : [];
  if (blockIds.length > 0) {
    return blockIds.map((id) => `Блок #${id}`);
  }
  return [];
};

export const getCurrentWorkflowStage = (agreement) => {
  const stages = Array.isArray(agreement?.workflow_stages)
    ? agreement.workflow_stages
    : [];
  if (stages.length === 0) return null;

  const inProgressIndex = stages.findIndex((s) => s?.status === "in_progress");
  if (inProgressIndex >= 0) {
    return { stage: stages[inProgressIndex], order: inProgressIndex + 1 };
  }

  const firstPendingIndex = stages.findIndex(
    (s) => s?.status !== "completed" && s?.status !== "rejected",
  );
  if (firstPendingIndex >= 0) {
    return { stage: stages[firstPendingIndex], order: firstPendingIndex + 1 };
  }

  const lastIndex = stages.length - 1;
  return { stage: stages[lastIndex], order: lastIndex + 1 };
};

export const resolvePaymentStatus = (agreement) => {
  const payments = Array.isArray(agreement?.payments) ? agreement.payments : [];
  const priority = [
    "overdue_advance",
    "overdue_OPGE",
    "overdue_ODGE",
    "overdue",
    "paid_advance_overdue",
    "paid_overdue",
    "expected_advance",
    "expected_ODGE",
    "expected_OPGE",
    "expected",
    "paid_advance",
    "paid",
  ];

  for (const key of priority) {
    const match = payments.find((p) => p?.status === key);
    if (match) return key;
  }

  if (payments.length > 0) {
    const last = payments[payments.length - 1];
    if (last?.status) return last.status;
  }

  return agreement?.status ?? null;
};

export const getPaymentStatusVariant = (status) => {
  if (!status) return "neutral";
  if (
    status.includes("overdue") &&
    !status.includes("paid")
  ) {
    return "overdue";
  }
  if (status.includes("paid_overdue") || status === "paid_advance_overdue") {
    return "paid-late";
  }
  if (status.includes("paid")) return "paid";
  if (status.includes("expected")) return "expected";
  return "neutral";
};

export const formatCurrency = (amount) => {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return `${new Intl.NumberFormat("ru-RU").format(Number(amount))} ₸`;
};

export const formatCurrencyPlain = (amount) => {
  if (amount == null || Number.isNaN(Number(amount))) return null;
  return new Intl.NumberFormat("ru-RU").format(Number(amount));
};

export const formatAgreementNumber = (num) => {
  if (num == null) return "—";
  const parsed = parseInt(String(num).replace(/\D/g, ""), 10);
  if (Number.isNaN(parsed)) return String(num);
  return `№${String(parsed).padStart(7, "0")}`;
};

export const PERFORMER_TYPE_LABELS = {
  staff_employee: "Штатный сотрудник",
  contract_employee: "Сотрудник на подряд",
  gph_physical: "Физ лицо на ГПХ",
  counterparty: "Контрагент",
  too_with_vat: "ТОО с НДС",
  too_without_vat: "ТОО без НДС",
  ip: "ИП",
};

export const PAYMENT_SCHEDULE_STAGE_KEYS = ["advance", "odge", "opge"];

export const PAYMENT_STAGE_TEMPLATES = [
  { key: "advance", label: "Аванс", percent: 30 },
  { key: "odge", label: "Оплата до госэкспертизы (ОДГЭ)", percent: 50 },
  { key: "opge", label: "Оплата после госэкспертизы (ОПГЭ)", percent: 20 },
];

const normalizePaymentTypeKey = (paymentType) =>
  String(paymentType || "")
    .trim()
    .toLowerCase();

export const hasPaymentScheduleData = (agreement) => {
  const schedule = agreement?.payment_schedule || {};
  if (Array.isArray(schedule.percentages) && schedule.percentages.length > 0) {
    return true;
  }
  return PAYMENT_SCHEDULE_STAGE_KEYS.some(
    (key) => schedule[key] != null || schedule[`${key}_percent`] != null,
  );
};

const EXTRA_PAYMENT_TRANCHE_ORDINALS = [
  "Четвёртый",
  "Пятый",
  "Шестой",
  "Седьмой",
  "Восьмой",
  "Девятый",
  "Десятый",
];

/** Шаблон этапа по индексу: базовые [аванс, ОДГЭ, ОПГЭ], далее — доп. транши. */
export const getPaymentStageTemplate = (index) => {
  if (PAYMENT_STAGE_TEMPLATES[index]) return PAYMENT_STAGE_TEMPLATES[index];
  const ordinal =
    EXTRA_PAYMENT_TRANCHE_ORDINALS[index - PAYMENT_STAGE_TEMPLATES.length];
  return {
    key: `stage_${index + 1}`,
    label: ordinal ? `${ordinal} транш` : `Транш ${index + 1}`,
    percent: 0,
  };
};

/** Проценты по этапам [аванс, ОДГЭ, ОПГЭ, ...доп. этапы] из payment_schedule */
export const getPaymentSchedulePercentages = (agreement) => {
  const schedule = agreement?.payment_schedule || {};
  const fromArray = Array.isArray(schedule.percentages) ? schedule.percentages : null;

  if (fromArray && fromArray.length > 0) {
    const length = Math.max(PAYMENT_SCHEDULE_STAGE_KEYS.length, fromArray.length);
    return Array.from({ length }, (_, index) => ({
      key: getPaymentStageTemplate(index).key,
      percent: Number(fromArray[index]) || 0,
    }));
  }

  return PAYMENT_SCHEDULE_STAGE_KEYS.map((key, index) => {
    const template = PAYMENT_STAGE_TEMPLATES[index];
    const percent = Number(
      schedule[key] ?? schedule[`${key}_percent`] ?? template?.percent ?? 0,
    );
    return { key, percent: Number.isFinite(percent) ? percent : 0 };
  });
};

/** Название этапа, введённое вручную для «Своего графика» (payment_schedule.labels) */
export const getPaymentScheduleDescription = (agreement, index) => {
  const labels = agreement?.payment_schedule?.labels;
  if (!Array.isArray(labels)) return null;
  const value = String(labels[index] ?? "").trim();
  return value || null;
};

export const countWorkingDays = (start, end) => {
  if (!start || !end) return null;
  const startDate = parseApiDateValue(start) ?? new Date(start);
  const endDate = parseApiDateValue(end) ?? new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  if (endDate < startDate) return 0;
  let count = 0;
  const cursor = new Date(startDate);
  cursor.setHours(0, 0, 0, 0);
  const endD = new Date(endDate);
  endD.setHours(0, 0, 0, 0);
  while (cursor <= endD) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

export const formatDateRu = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = parseApiDateValue(dateStr) ?? new Date(dateStr);
    return Number.isNaN(d.getTime())
      ? dateStr
      : d.toLocaleDateString("ru-RU", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  } catch {
    return dateStr;
  }
};

export const formatDateRangeRu = (dateFrom, dateTo) => {
  const from = formatDateRu(dateFrom);
  const to = formatDateRu(dateTo);
  if (!from && !to) return null;
  if (!from || !to) return from || to;
  const days = countWorkingDays(dateFrom, dateTo);
  const range = `${from} - ${to}`;
  return days != null ? `${range} (${days} рабочих дней)` : range;
};

export const getAgreementBlocksRows = (agreement) => {
  const details = Array.isArray(agreement?.agreement_blocks_detail)
    ? agreement.agreement_blocks_detail
    : [];
  if (details.length > 0) {
    return details.map((block) => ({
      id: block.id ?? block.block_id,
      name: block.block_name || `Блок #${block.block_id}`,
      price: block.price,
    }));
  }
  const blocks = Array.isArray(agreement?.agreement_blocks)
    ? agreement.agreement_blocks
    : [];
  return blocks.map((block) => ({
    id: block.id ?? block.block_id,
    name: `Блок #${block.block_id}`,
    price: block.price,
  }));
};

export const buildPaymentRows = (agreement) => {
  const payments = Array.isArray(agreement?.payments) ? agreement.payments : [];
  const scheduleDates = Array.isArray(agreement?.payment_schedule?.planned_dates)
    ? agreement.payment_schedule.planned_dates
    : [];
  const totalPrice = Number(agreement?.price) || 0;
  const hasSchedule = hasPaymentScheduleData(agreement);
  const stageTemplates = hasSchedule
    ? getPaymentSchedulePercentages(agreement).map(({ percent }, index) => ({
        ...getPaymentStageTemplate(index),
        percent,
      }))
    : PAYMENT_STAGE_TEMPLATES;

  const standardTemplateKey = hasSchedule
    ? resolvePaymentTemplateFromPercentages(
        stageTemplates.map((template) => template.percent),
      )
    : null;

  return stageTemplates.map((template, index) => {
    const percent = template.percent;

    if (hasSchedule && percent === 0) {
      return null;
    }

    const payment =
      payments[index] ||
      payments.find(
        (p) => normalizePaymentTypeKey(p?.payment_type) === template.key,
      );
    const plannedAmount =
      payment?.amount != null
        ? Number(payment.amount)
        : totalPrice > 0
          ? Math.round((totalPrice * percent) / 100)
          : null;

    return {
      ...template,
      label:
        getPaymentScheduleDescription(agreement, index) ||
        getPaymentTemplateStageLabel(standardTemplateKey, index) ||
        template.label,
      percent,
      payment,
      plannedAmount,
      plannedDate: payment?.planned_date || scheduleDates[index] || null,
      paidAt: payment?.paid_at,
      status: payment?.status,
    };
  }).filter(Boolean);
};

export const getPaymentRowUiState = (status) => {
  if (!status) return { icon: "pending", label: "Не оплачено" };
  if (status.includes("paid") && !status.includes("overdue")) {
    return { icon: "paid", label: PAYMENT_STATUS_LABELS[status] ?? "Оплачен" };
  }
  if (status.includes("overdue")) {
    return { icon: "overdue", label: PAYMENT_STATUS_LABELS[status] ?? "Просрочен" };
  }
  if (status.includes("expected")) {
    return { icon: "expected", label: PAYMENT_STATUS_LABELS[status] ?? "Ожидается" };
  }
  return { icon: "pending", label: PAYMENT_STATUS_LABELS[status] ?? status };
};

export const matchesSearchQuery = (
  agreement,
  query,
  counterpartyNameById = {},
  performerNameById = {},
) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = getCounterpartyDisplayName(
    agreement,
    counterpartyNameById,
    performerNameById,
  ).toLowerCase();
  return (
    String(agreement.number ?? "").includes(q) ||
    String(agreement.id ?? "").includes(q) ||
    name.includes(q) ||
    String(agreement.company_id ?? "").includes(q) ||
    String(agreement.counterparty_id ?? "").includes(q)
  );
};

export const matchesSectionFilter = (agreement, sectionFilter) => {
  if (!sectionFilter) return true;
  const labels = getAgreementSectionLabels(agreement);
  return labels.some((label) => label === sectionFilter);
};

export const matchesStageFilter = (agreement, stageFilter) => {
  if (!stageFilter) return true;
  const current = getCurrentWorkflowStage(agreement);
  return current?.stage?.stage_name === stageFilter;
};

export const matchesPaymentFilter = (agreement, paymentFilter) => {
  if (!paymentFilter) return true;
  return resolvePaymentStatus(agreement) === paymentFilter;
};
